import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ExcessPaymentsService, ExcessPaymentResponseDTO, PaymentGroupSummaryDTO } from '../../../services/excess-payments.service';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-view-excess-payment',
  templateUrl: './view-excess-payment.component.html',
  styleUrls: ['./view-excess-payment.component.scss']
})
export class ViewExcessPaymentComponent implements OnInit {
  excessPayment: ExcessPaymentResponseDTO | null = null;
  paymentGroupSummary: PaymentGroupSummaryDTO | null = null;
  loading = false;
  loadingFinancialSummary = false;
  excessPaymentId: number = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private excessPaymentsService: ExcessPaymentsService,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.excessPaymentId = +params['id'];
      if (this.excessPaymentId) {
        this.loadExcessPayment();
      }
    });
  }

  loadExcessPayment(): void {
    this.loading = true;
    this.excessPaymentsService.getById(this.excessPaymentId).subscribe({
      next: (result) => {
        if (result.isSuccess) {
          this.excessPayment = result.data;
          
          // Cargar resumen financiero del grupo de pagos
          if (this.excessPayment.originIncomeReceipt?.paymentGroupId) {
            this.loadPaymentGroupSummary(this.excessPayment.originIncomeReceipt.paymentGroupId);
          } else {
            this.loading = false;
            this.toastService.showToast('warning', 'No se encontró información del grupo de pagos para este excedente');
          }
        } else {
          this.toastService.showToast('error', result.errorMessage || 'Error al cargar el excedente');
          this.router.navigate(['/dashboard/excess-payments']);
          this.loading = false;
        }
      },
      error: (error) => {
        console.error('Error loading excess payment:', error);
        this.toastService.showToast('error', 'Error al cargar el excedente');
        this.router.navigate(['/dashboard/excess-payments']);
        this.loading = false;
      }
    });
  }

  loadPaymentGroupSummary(paymentGroupId: string): void {
    this.loadingFinancialSummary = true;
    
    this.excessPaymentsService.getPaymentGroupSummary(paymentGroupId).subscribe({
      next: (result) => {
        if (result.isSuccess) {
          this.paymentGroupSummary = result.data;
          this.toastService.showToast('success', 'Resumen financiero cargado exitosamente');
        } else {
          this.toastService.showToast('error', result.errorMessage || 'Error al cargar el resumen financiero');
        }
        this.loading = false;
        this.loadingFinancialSummary = false;
      },
      error: (error) => {
        console.error('Error loading payment group summary:', error);
        this.toastService.showToast('error', 'Error al cargar el resumen financiero');
        this.loading = false;
        this.loadingFinancialSummary = false;
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/dashboard/excess-payments']);
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'available':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'refunded':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'expired':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  }

  formatCurrency(amount: number, currencyCode?: string): string {
    const code = currencyCode || this.excessPayment?.enrollment?.currencyCode || 'COP';
    const minimumFractionDigits = code === 'COP' ? 0 : 2;
    const maximumFractionDigits = code === 'COP' ? 0 : 2;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: code,
      minimumFractionDigits,
      maximumFractionDigits
    }).format(amount ?? 0);
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getTotalFeeDetails(): number {
    if (!this.excessPayment?.originIncomeReceipt?.totalFees) {
      return 0;
    }
    return this.excessPayment.originIncomeReceipt.totalFees;
  }


  /**
   * Obtener clase CSS para el estado del plan de pago
   */
  getPaymentStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'partially_paid':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  /**
   * Obtener clase CSS para el estado del participante
   */
  getParticipantStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'missing_documents':
        return 'bg-orange-100 text-orange-800';
      case 'graduated':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  /**
   * Obtener clase CSS para el tipo de proyecto
   */
  getProjectTypeClass(type: string): string {
    switch (type.toLowerCase()) {
      case 'diploma':
        return 'bg-purple-100 text-purple-800';
      case 'certificate':
        return 'bg-blue-100 text-blue-800';
      case 'course':
        return 'bg-green-100 text-green-800';
      case 'workshop':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  /**
   * Obtener clase CSS para el estado del proyecto
   */
  getProjectStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'created':
        return 'bg-blue-100 text-blue-800';
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-purple-100 text-purple-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'suspended':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  /**
   * Obtener ruta para navegación a inscripción (individual o corporativa)
   */
  getEnrollmentRoute(): string[] {
    if (!this.excessPayment?.enrollment) {
      return ['/enrollments'];
    }

    // Como no tenemos información completa del tipo de inscripción en el DTO,
    // usaremos la ruta individual por defecto
    // En el futuro se puede mejorar cuando el backend incluya más información del enrollment
    return ['/enrollments/view', this.excessPayment.enrollment.id.toString()];
  }

  // 🆕 FUNCIONES AUXILIARES PARA EXCEDENTES CORPORATIVOS
  getPayerName(excessPayment: ExcessPaymentResponseDTO): string {
    if (excessPayment.participant) {
      return `${excessPayment.participant.firstName} ${excessPayment.participant.lastName}`;
    } else if (excessPayment.company) {
      return excessPayment.company.name;
    }
    return 'Sin información';
  }

  getPayerDocumentId(excessPayment: ExcessPaymentResponseDTO): string {
    if (excessPayment.participant) {
      return excessPayment.participant.documentId;
    } else if (excessPayment.company) {
      return excessPayment.company.documentId;
    }
    return 'N/A';
  }

  getPayerType(excessPayment: ExcessPaymentResponseDTO): string {
    return excessPayment.isCorporatePayment ? '🏢 Corporativo' : '👤 Individual';
  }

  getCurrencyCode(excessPayment: ExcessPaymentResponseDTO): string {
    if (excessPayment.enrollment?.currencyCode) {
      return excessPayment.enrollment.currencyCode;
    } else if (excessPayment.corporateEnrollment?.currencyCode) {
      return excessPayment.corporateEnrollment.currencyCode;
    }
    return 'COP'; // Default
  }

}